/**
 * IndexedDB image cache for story/comic images.
 *
 * Reuses the existing storage.web.ts infrastructure (registerDb, getById, put, etc.)
 * to cache image blobs keyed by imageId with a 7-day expiry.
 */

import { registerDb, getById, put, getAll, remove } from "./storage.web";

const DB_NAME = "images";
const STORE_NAME = "images";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Register the DB/store on module load
registerDb(DB_NAME, [STORE_NAME]);

interface CachedImage {
  id: string;
  data: string;
  storedAt: number;
}

function isExpired(entry: CachedImage): boolean {
  return Date.now() - entry.storedAt > MAX_AGE_MS;
}

export async function getCachedImage(imageId: string): Promise<string | null> {
  try {
    const entry = await getById<CachedImage>(DB_NAME, STORE_NAME, imageId);
    if (!entry) return null;
    if (isExpired(entry)) {
      // Lazy-delete expired entry
      remove(DB_NAME, STORE_NAME, imageId).catch(() => {});
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

export async function setCachedImage(imageId: string, base64: string): Promise<void> {
  try {
    await put<CachedImage>(DB_NAME, STORE_NAME, {
      id: imageId,
      data: base64,
      storedAt: Date.now(),
    });
  } catch {
    // Silently fail — cache is best-effort
  }
}

export async function getCachedImages(imageIds: string[]): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  if (imageIds.length === 0) return result;

  const results = await Promise.all(imageIds.map((id) => getCachedImage(id)));
  for (let i = 0; i < imageIds.length; i++) {
    if (results[i]) {
      result.set(imageIds[i], results[i]!);
    }
  }
  return result;
}

export async function cleanupExpiredImages(): Promise<void> {
  try {
    const all = await getAll<CachedImage>(DB_NAME, STORE_NAME);
    const expired = all.filter(isExpired);
    await Promise.all(expired.map((entry) => remove(DB_NAME, STORE_NAME, entry.id)));
    if (expired.length > 0) {
      console.log(`[imageCache] Cleaned up ${expired.length} expired images`);
    }
  } catch {
    // Silently fail
  }
}
